import { useTranslation } from 'react-i18next';

import { Button, Field, Input, Textarea } from '@/shared/ui';
import { SERIE_TRANSPORT_OPTIONS } from '../serieDetailFormSchema';
import type { SerieDetailFormApi } from '../useSerieDetailForm';

interface SerieBasicsFormProps {
  form: SerieDetailFormApi;
  /** Read-only rozsah seriálu (odvozený z termínů). */
  dateRange: string;
}

function NumberSuffixInput({
  value,
  onChange,
  onBlur,
  invalid,
  suffix,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  invalid?: boolean;
  suffix: string;
}) {
  return (
    <span className="relative inline-flex w-[78px]">
      <Input
        type="number"
        min={1}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        invalid={invalid}
        className="pr-8"
      />
      <span className="text-phx-xs text-phx-text-muted pointer-events-none absolute top-1/2 right-[9px] -translate-y-1/2">
        {suffix}
      </span>
    </span>
  );
}

/**
 * Formulář „Údaje o termínovém seriálu“ (schválená varianta labely vlevo) nad TanStack Form.
 * Hodnoty i validaci drží `useSerieDetailForm`; tento komponent jen renderuje pole.
 */
export function SerieBasicsForm({ form, dateRange }: SerieBasicsFormProps) {
  const { t } = useTranslation('planner');

  return (
    <div className="gap-x-phx-7 gap-y-phx-3 grid grid-cols-1 md:grid-cols-2">
      <form.Field name="name">
        {(field) => {
          const error = field.state.meta.isTouched
            ? (field.state.meta.errors[0]?.message as string | undefined)
            : undefined;
          return (
            <Field layout="inline" label={t('serieDetail.basics.name')} required error={error}>
              <Input
                value={field.state.value}
                onChange={(event) => field.handleChange(event.target.value)}
                onBlur={field.handleBlur}
                required
                invalid={Boolean(error)}
              />
            </Field>
          );
        }}
      </form.Field>

      <form.Field name="transportType">
        {(field) => (
          <Field layout="inline" label={t('serieDetail.basics.transport')}>
            <div className="flex flex-wrap gap-1" role="group">
              {SERIE_TRANSPORT_OPTIONS.map((option) => (
                <Button
                  key={option}
                  type="button"
                  size="sm"
                  variant={field.state.value === option ? 'primary' : 'outline'}
                  onClick={() => field.handleChange(option)}
                >
                  {t(`serie.transport.${option}`)}
                </Button>
              ))}
            </div>
          </Field>
        )}
      </form.Field>

      <form.Field name="capacityPerTerm">
        {(capacityField) => (
          <form.Field name="mainProgramLengthDays">
            {(lengthField) => {
              const capacityError = capacityField.state.meta.isTouched
                ? (capacityField.state.meta.errors[0]?.message as string | undefined)
                : undefined;
              const lengthError = lengthField.state.meta.isTouched
                ? (lengthField.state.meta.errors[0]?.message as string | undefined)
                : undefined;
              return (
                <Field
                  layout="inline"
                  label={t('serieDetail.basics.capacityPerTerm')}
                  error={capacityError ?? lengthError}
                >
                  <div className="gap-phx-2 flex items-center">
                    <NumberSuffixInput
                      value={capacityField.state.value}
                      onChange={capacityField.handleChange}
                      onBlur={capacityField.handleBlur}
                      invalid={Boolean(capacityError)}
                      suffix={t('serieDetail.basics.paxSuffix')}
                    />
                    <span className="text-phx-sm text-phx-text-2 ml-1 font-medium whitespace-nowrap">
                      {t('serieDetail.basics.length')}
                    </span>
                    <NumberSuffixInput
                      value={lengthField.state.value}
                      onChange={lengthField.handleChange}
                      onBlur={lengthField.handleBlur}
                      invalid={Boolean(lengthError)}
                      suffix={t('serieDetail.basics.daysSuffix')}
                    />
                  </div>
                </Field>
              );
            }}
          </form.Field>
        )}
      </form.Field>

      <Field layout="inline" label={t('serieDetail.basics.range')}>
        <div className="text-phx-md text-phx-text py-[5px] tabular-nums">{dateRange}</div>
      </Field>

      <form.Field name="note">
        {(field) => (
          <Field layout="inline" label={t('serieDetail.basics.note')} className="md:col-span-2">
            <Textarea
              rows={2}
              value={field.state.value}
              onChange={(event) => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
            />
          </Field>
        )}
      </form.Field>
    </div>
  );
}
